import { html } from 'lit';
import { loading } from '../../../common/constants/api-constants';

/**
 * Utility functions for tabbed search components
 */

/**
 * Map tab names to their corresponding business units
 */
export function getBusinessUnit(tab: string, tabMapping: Record<string, string>): string {
  return tabMapping[tab] || 'unknown';
}

/**
 * Get appropriate ARIA label based on active tab
 */
export function getSearchLabel(activeTab: string): string {
  const labels = {
    'Product': 'Product Search',
    'Support': 'Support Search',
    'Articles': 'Articles Search'
  };
  return labels[activeTab] || 'Search';
}

/**
 * Check if a scroll element is scrollable and return scroll states
 */
export function checkScrollState(element: HTMLElement | null): {
  isScrollable: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
} {
  if (!element) {
    return { isScrollable: false, canScrollLeft: false, canScrollRight: false };
  }
  
  // Check if scrollWidth is significantly larger than clientWidth (add buffer for fractional pixels)
  const isScrollable = element.scrollWidth > element.clientWidth + 5;
  
  // Check if can scroll left (not at the beginning)
  const canScrollLeft = element.scrollLeft > 5;
  
  // Check if can scroll right (not at the end)
  const canScrollRight = Math.ceil(element.scrollLeft + element.clientWidth) < element.scrollWidth - 5;
  
  return { isScrollable, canScrollLeft, canScrollRight };
}

/**
 * Create a debounced function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  
  return (...args: Parameters<T>): void => {
    if (timeout !== null) {
      window.clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Update URL parameters
 * @param params - Map of parameters to update
 */
export function updateUrlParams(params: Map<string, string>): void {
  const url = new URL(window.location.href);
  
  // Loop through the map and set each parameter in the URL
  params.forEach((value, key) => {
    url.searchParams.set(key, value);
  });
  
  history.pushState({}, '', url.toString());
}

/**
 * Handles keyboard navigation for accessibility
 * @param event - Keyboard event
 * @param action - Action to perform
 */
export function handleKeyboardNavigation(event: KeyboardEvent, action: () => void): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

/**
 * Generates a loading template for search results
 * @returns Loading indicator template with accessibility support
 */
export function generateLoadingTemplate() {
  return html`
    <div class="loading-indicator" aria-live="polite">
      <span class="visually-hidden">Loading search results</span>
      ${loading({ width: '100%', height: '4px', m: 'mb-4' })}
      <div class="loading-container d-flex flex-column mb-4">
        ${loading({ height: '150px' })}
      </div>
      <div class="loading-container d-flex flex-column mb-4">
        ${loading({ height: '150px' })}
      </div>
      <div class="loading-container d-flex flex-column mb-4">
        ${loading({ height: '150px' })}
      </div>
    </div>
  `;
}